import React, { useState, useEffect, useMemo } from "react";
import { Link } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from '../lib/queryClient';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../hooks/use-auth';
import { formatCurrency } from '../lib/utils';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import {
  FileText,
  Users,
  ClipboardList,
  Calendar,
  MoreVertical,
  Activity,
  Download,
  MapPin,
  DollarSign,
  TrendingUp,
} from "lucide-react";
import { format, subMonths, startOfMonth, endOfMonth, eachMonthOfInterval } from "date-fns";

import Sidebar from '../components/layout/sidebar';
import MobileSidebar from '../components/layout/mobile-sidebar';
import TopNav from '../components/layout/top-nav';
import ProjectsMap from '../components/dashboard/ProjectsMap';

const CHART_COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#64748b'];

export default function Dashboard() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { refreshSession } = useAuth();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment');
    const plan = urlParams.get('plan');
    const trial = urlParams.get('trial');
    if (paymentSuccess === 'success' && plan) {
      toast({
        title: trial === 'true' ? "🎉 Trial Started!" : "🎉 Payment Successful!",
        description: trial === 'true'
          ? `Welcome! Choose a plan below to start your 7-day free trial.`
          : `Welcome to the ${plan.charAt(0).toUpperCase() + plan.slice(1)} plan! Your subscription is now active.`,
        duration: 7000,
      });
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      setTimeout(async () => {
        try {
          const res = await fetch("/api/user", { credentials: "include", headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` } });
          if (res.ok) {
            const userData = await res.json();
            const user = userData.user || userData;
            localStorage.setItem('user', JSON.stringify(user));
            queryClient.setQueryData(["/api/user"], user);
            window.location.reload();
          }
        } catch (e) {
          window.location.reload();
        }
      }, 2000);
    }
  }, [toast, queryClient]);

  const { data: clients = [] } = useQuery({ queryKey: ['/api/protected/clients'], queryFn: async () => (await apiRequest("GET", "/api/protected/clients")).json() });
  const { data: projects = [] } = useQuery({ queryKey: ['/api/protected/projects'], queryFn: async () => (await apiRequest("GET", "/api/protected/projects")).json() });
  const { data: estimates = [] } = useQuery({ queryKey: ['/api/protected/estimates'], queryFn: async () => (await apiRequest("GET", "/api/protected/estimates")).json() });
  const { data: invoices = [] } = useQuery({ queryKey: ['/api/protected/invoices'], queryFn: async () => (await apiRequest("GET", "/api/protected/invoices")).json() });
  const { data: events = [] } = useQuery({ queryKey: ['/api/protected/events'], queryFn: async () => (await apiRequest("GET", "/api/protected/events")).json() });

  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
  const isCurrentMonth = (d: any) => { const x = new Date(d); return !isNaN(x.getTime()) && x.getMonth() === currentMonth && x.getFullYear() === currentYear; };
  const isLastMonth = (d: any) => { const x = new Date(d); return !isNaN(x.getTime()) && x.getMonth() === lastMonth && x.getFullYear() === lastMonthYear; };

  const currentMonthRevenue = invoices.filter((inv: any) => isCurrentMonth(inv.created_at || inv.createdAt || inv.issue_date || inv.issueDate))
    .reduce((s: number, inv: any) => s + (parseFloat(inv.total_amount) || 0), 0);
  const lastMonthRevenue = invoices.filter((inv: any) => isLastMonth(inv.created_at || inv.createdAt || inv.issue_date || inv.issueDate))
    .reduce((s: number, inv: any) => s + (parseFloat(inv.total_amount) || 0), 0);
  const revenueChange = lastMonthRevenue > 0 ? Math.round(((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100) : (currentMonthRevenue > 0 ? 100 : 0);

  const currentMonthOrders = events.filter((e: any) => {
    const dt = e.startTime ? new Date(e.startTime) : e.start_time ? new Date(e.start_time) : e.startDate ? new Date(e.startDate) : e.start_date_string ? new Date(e.start_date_string) : null;
    return dt && !isNaN(dt.getTime()) && isCurrentMonth(dt);
  }).length;
  const lastMonthOrders = events.filter((e: any) => {
    const dt = e.startTime ? new Date(e.startTime) : e.start_time ? new Date(e.start_time) : e.startDate ? new Date(e.startDate) : e.start_date_string ? new Date(e.start_date_string) : null;
    return dt && !isNaN(dt.getTime()) && isLastMonth(dt);
  }).length;
  const ordersChange = lastMonthOrders > 0 ? Math.round(((currentMonthOrders - lastMonthOrders) / lastMonthOrders) * 100) : (currentMonthOrders > 0 ? 100 : 0);

  const totalValue = [...estimates, ...invoices].reduce((s, x) => s + (parseFloat(x.total) || parseFloat(x.total_amount) || 0), 0);
  const countValue = estimates.length + invoices.length;
  const avgContract = countValue > 0 ? totalValue / countValue : 0;
  const lastMonthTotal = invoices.filter((inv: any) => isLastMonth(inv.created_at || inv.createdAt || inv.issue_date || inv.issueDate))
    .reduce((s: number, inv: any) => s + (parseFloat(inv.total_amount) || 0), 0);
  const avgChange = lastMonthTotal > 0 ? Math.round(((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100) : 0;

  const totalRevenue = invoices.reduce((s: number, inv: any) => s + (parseFloat(inv.total_amount) || 0), 0);
  const growthRate = lastMonthRevenue > 0 ? ((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue * 100).toFixed(2) : '0';
  const growthChange = lastMonthRevenue > 0 ? ((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue * 100 - (lastMonthRevenue > 0 ? 0 : 0)).toFixed(2) : '0';

  const monthlyChartData = useMemo(() => {
    const months = eachMonthOfInterval({ start: subMonths(now, 11), end: now });
    return months.map((m) => {
      const invs = invoices.filter((inv: any) => {
        const d = new Date(inv.created_at || inv.createdAt || inv.issue_date || inv.issueDate);
        return !isNaN(d.getTime()) && d.getMonth() === m.getMonth() && d.getFullYear() === m.getFullYear();
      });
      const evts = events.filter((e: any) => {
        const dt = e.startTime ? new Date(e.startTime) : e.start_time ? new Date(e.start_time) : e.startDate ? new Date(e.startDate) : e.start_date_string ? new Date(e.start_date_string) : null;
        return dt && !isNaN(dt.getTime()) && dt.getMonth() === m.getMonth() && dt.getFullYear() === m.getFullYear();
      });
      return {
        month: format(m, 'MMM yy'),
        Revenue: invs.reduce((s: number, i: any) => s + (parseFloat(i.total_amount) || 0), 0),
        Orders: evts.length,
      };
    });
  }, [invoices, events, now]);

  const activityPieData = useMemo(() => {
    const draft = estimates.filter((e: any) => e.status === 'draft').length;
    const sent = estimates.filter((e: any) => e.status === 'sent').length;
    const accepted = estimates.filter((e: any) => e.status === 'accepted').length;
    const converted = invoices.filter((i: any) => i.status === 'paid').length;
    const inProgress = projects.filter((p: any) => p.status === 'in_progress').length;
    const completed = projects.filter((p: any) => p.status === 'completed').length;
    const total = draft + sent + accepted + converted + inProgress + completed;
    const pct = (v: number) => (total > 0 ? Math.round((v / total) * 100) : 0);
    return [
      { name: 'Draft', value: draft, color: CHART_COLORS[0], percent: pct(draft) },
      { name: 'Sent', value: sent, color: CHART_COLORS[1], percent: pct(sent) },
      { name: 'Accepted', value: accepted, color: CHART_COLORS[2], percent: pct(accepted) },
      { name: 'Paid', value: converted, color: CHART_COLORS[3], percent: pct(converted) },
      { name: 'In Progress', value: inProgress, color: CHART_COLORS[4], percent: pct(inProgress) },
      { name: 'Completed', value: completed, color: CHART_COLORS[5], percent: pct(completed) },
    ].filter((x) => x.value > 0);
  }, [estimates, invoices, projects]);

  const topDeals = useMemo(() => {
    const combined = [
      ...estimates.map((e: any) => ({ ...e, value: parseFloat(e.total) || 0, type: 'estimate' })),
      ...invoices.map((i: any) => ({ ...i, value: parseFloat(i.total_amount) || 0, type: 'invoice' })),
    ];
    return combined.sort((a, b) => b.value - a.value).slice(0, 5);
  }, [estimates, invoices]);

  // All projects that are not completed or cancelled — show on map
  const projectsForMap = useMemo(() => {
    const s = (status: string) => (status || '').toLowerCase();
    return projects.filter((p: any) => s(p.status) !== 'completed' && s(p.status) !== 'cancelled');
  }, [projects]);

  const projectsByLocation = useMemo(() => {
    const map = new Map<string, { count: number; projects: any[] }>();
    projects.forEach((p: any) => {
      const client = p.client || clients.find((c: any) => c.id === p.clientId || c.id === p.client_id);
      const city = client?.city || client?.address || 'Unknown';
      const state = client?.state || '';
      const key = (city && state) ? `${city}, ${state}` : city || 'Unknown';
      if (!map.has(key)) map.set(key, { count: 0, projects: [] });
      map.get(key)!.count++;
      map.get(key)!.projects.push(p);
    });
    return Array.from(map.entries()).map(([location, { count }]) => ({ location, count })).sort((a, b) => b.count - a.count).slice(0, 8);
  }, [projects, clients]);

  const getEventDate = (e: any) => {
    if (e.startTime) return new Date(e.startTime);
    if (e.start_time) return new Date(e.start_time);
    if (e.startDate) return new Date(e.startDate);
    if (e.start_date_string) return new Date(e.start_date_string);
    return new Date(0);
  };

  const upcomingEvents = useMemo(() => {
    const valid = events
      .map((e: any) => ({ ...e, date: getEventDate(e) }))
      .filter((e: any) => !isNaN(e.date.getTime()) && e.date >= new Date())
      .sort((a: any, b: any) => a.date.getTime() - b.date.getTime());
    return valid.slice(0, 5);
  }, [events]);

  const recentActivities = useMemo(() => {
    const items: { id: string; title: string; desc: string; date: Date }[] = [];
    estimates.slice(0, 3).forEach((e: any) => {
      const c = clients.find((x: any) => x.id === e.clientId || x.id === e.client_id);
      items.push({ id: `est-${e.id}`, title: 'Estimate created', desc: `${e.title || 'Estimate'} - ${c ? (c.firstName || c.first_name) + ' ' + (c.lastName || c.last_name) : 'Client'}`, date: new Date(e.created_at || e.createdAt) });
    });
    invoices.slice(0, 3).forEach((i: any) => {
      const c = clients.find((x: any) => x.id === i.clientId || x.id === i.client_id);
      items.push({ id: `inv-${i.id}`, title: i.status === 'paid' ? 'Payment received' : 'Invoice created', desc: `${i.invoice_number || i.invoiceNumber || 'Invoice'} - ${c ? (c.firstName || c.first_name) + ' ' + (c.lastName || c.last_name) : 'Client'}`, date: new Date(i.created_at || i.createdAt) });
    });
    clients.slice(0, 2).forEach((c: any) => {
      items.push({ id: `cli-${c.id}`, title: 'New client', desc: `${c.firstName || c.first_name} ${c.lastName || c.last_name}`, date: new Date(c.createdAt || c.created_at) });
    });
    return items.sort((a, b) => b.date.getTime() - a.date.getTime()).slice(0, 5);
  }, [estimates, invoices, clients]);

  const handleDownloadReport = () => {
    toast({ title: 'Report', description: 'Download report feature coming soon.' });
  };

  const handleRequestReport = () => {
    toast({ title: 'Custom Report', description: 'Request custom report feature coming soon.' });
  };

  const MetricCard = ({ title, value, change, changeLabel, icon }: { title: string; value: string | number; change?: number; changeLabel?: string; icon: React.ReactNode }) => (
    <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-[var(--dash-text-muted)]">{title}</p>
          <p className="text-2xl font-bold text-[var(--dash-text)] mt-1">{value}</p>
          {change != null && changeLabel && (
            <p className={`text-xs mt-1 ${change >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {change >= 0 ? '+' : ''}{change}% {changeLabel}
            </p>
          )}
        </div>
        <div className="rounded-lg p-2 bg-[var(--dash-accent)]/10 text-[var(--dash-accent)]">{icon}</div>
      </div>
    </div>
  );

  return (
    <div className="remodra-layout">
      <div className="hidden md:block"><Sidebar /></div>
      <MobileSidebar />
      <div className="remodra-main-mobile md:ml-64">
        <TopNav />
        <div className="remodra-content">
          <main className="dashboard-2025 bg-[var(--dash-bg)] min-h-screen p-6 md:p-8 pt-24">
            <div className="w-full space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-[var(--dash-text)]">Overall</h1>
                  <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">Business overview and key metrics</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="border-[var(--dash-border)]" onClick={handleDownloadReport}>
                    <Download className="h-4 w-4 mr-2" />Download report
                  </Button>
                  <Button variant="outline" size="sm" className="border-[var(--dash-border)]" onClick={handleRequestReport}>
                    Request custom report
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard title="Net Income" value={formatCurrency(currentMonthRevenue)} change={revenueChange} changeLabel="from last month" icon={<DollarSign className="h-5 w-5" />} />
                <MetricCard title="Orders per Month" value={currentMonthOrders} change={ordersChange} changeLabel="from last month" icon={<Calendar className="h-5 w-5" />} />
                <MetricCard title="Average Contract" value={formatCurrency(avgContract)} change={avgChange} changeLabel="from last month" icon={<FileText className="h-5 w-5" />} />
                <MetricCard title="Growth Rate" value={`${growthRate}%`} change={parseFloat(growthChange)} changeLabel="from last month" icon={<TrendingUp className="h-5 w-5" />} />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-[var(--dash-border)]">
                    <h2 className="text-lg font-semibold text-[var(--dash-text)]">Total Sales</h2>
                    <p className="text-sm text-[var(--dash-text-muted)]">Revenue and orders over time</p>
                  </div>
                  <div className="p-4 h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={monthlyChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--dash-border)" />
                        <XAxis dataKey="month" stroke="var(--dash-text-muted)" fontSize={12} />
                        <YAxis stroke="var(--dash-text-muted)" fontSize={12} tickFormatter={(v) => v >= 1000 ? `$${v/1000}k` : String(v)} />
                        <Tooltip formatter={(v: number, n: string) => [n === 'Revenue' ? formatCurrency(v) : v, n]} contentStyle={{ background: 'var(--dash-card)', border: '1px solid var(--dash-border)' }} />
                        <Legend />
                        <Line type="monotone" dataKey="Revenue" name="Revenue" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
                        <Line type="monotone" dataKey="Orders" name="Orders" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-[var(--dash-border)]">
                    <h2 className="text-lg font-semibold text-[var(--dash-text)]">Sales Activity</h2>
                    <p className="text-sm text-[var(--dash-text-muted)]">Estimates, invoices & projects by status</p>
                  </div>
                  <div className="p-4 h-[300px]">
                    {activityPieData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={activityPieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={90}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {activityPieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                          </Pie>
                          <Tooltip formatter={(v: number, name: string, props: any) => [`${v} (${props.payload.percent}%)`, name]} contentStyle={{ background: 'var(--dash-card)', border: '1px solid var(--dash-border)' }} />
                          <Legend formatter={(value, entry: any) => `${value} ${entry?.payload?.percent ?? 0}%`} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-[var(--dash-text-muted)]">No data yet</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-[var(--dash-border)] flex justify-between items-center">
                    <div>
                      <h2 className="text-lg font-semibold text-[var(--dash-text)]">Top Deals</h2>
                      <p className="text-sm text-[var(--dash-text-muted)]">Highest value estimates and invoices</p>
                    </div>
                    <Link to="/estimates"><Button variant="ghost" size="sm">View All</Button></Link>
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[var(--dash-border)]">
                          <TableHead className="text-[var(--dash-text-muted)]">No</TableHead>
                          <TableHead className="text-[var(--dash-text-muted)]">Client / Project</TableHead>
                          <TableHead className="text-[var(--dash-text-muted)]">Deal Value</TableHead>
                          <TableHead className="text-[var(--dash-text-muted)]">Type</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {topDeals.map((deal: any, i: number) => {
                          const c = clients.find((x: any) => x.id === deal.clientId || x.id === deal.client_id);
                          const name = c ? `${c.firstName || c.first_name} ${c.lastName || c.last_name}` : deal.title || 'Unknown';
                          return (
                            <TableRow key={deal.id} className="border-[var(--dash-border)]">
                              <TableCell className="font-medium">{i + 1}</TableCell>
                              <TableCell>{name}</TableCell>
                              <TableCell className="text-[var(--dash-accent)] font-semibold">{formatCurrency(deal.value)}</TableCell>
                              <TableCell><Badge variant="outline" className="text-xs">{deal.type === 'estimate' ? 'Estimate' : 'Invoice'}</Badge></TableCell>
                            </TableRow>
                          );
                        })}
                        {topDeals.length === 0 && (
                          <TableRow><TableCell colSpan={4} className="text-center py-8 text-[var(--dash-text-muted)]">No deals yet</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-[var(--dash-border)] flex justify-between items-center">
                    <div>
                      <h2 className="text-lg font-semibold text-[var(--dash-text)]">Projects by Location</h2>
                      <p className="text-sm text-[var(--dash-text-muted)]">Projects not completed or cancelled (zoom in/out)</p>
                    </div>
                    <Link to="/projects"><Button variant="ghost" size="sm">View All</Button></Link>
                  </div>
                  <div className="p-4">
                    {projects.length > 0 ? (
                      <ProjectsMap projects={projectsForMap} clients={clients} />
                    ) : (
                      <div className="h-[320px] flex items-center justify-center text-[var(--dash-text-muted)]">
                        <MapPin className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No projects to display on map</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-[var(--dash-border)]">
                    <h2 className="text-lg font-semibold text-[var(--dash-text)]">Upcoming Events</h2>
                    <p className="text-sm text-[var(--dash-text-muted)]">Scheduled visits and meetings</p>
                  </div>
                  <div className="p-6">
                    <ScrollArea className="h-[280px] pr-4">
                      {upcomingEvents.length > 0 ? (
                        <div className="space-y-4">
                          {upcomingEvents.map((event: any) => {
                            const client = clients.find((c: any) => c.id === event.clientId || c.id === event.client_id);
                            return (
                              <div key={event.id} className="flex gap-4 items-start">
                                <div className="rounded-full p-2 bg-[var(--dash-accent)]/10 text-[var(--dash-accent)]"><Calendar className="h-4 w-4" /></div>
                                <div className="flex-1">
                                  <div className="flex justify-between">
                                    <p className="font-medium text-[var(--dash-text)]">{event.title || (client ? `${client.firstName || client.first_name} ${client.lastName || client.last_name}` : 'Event')}</p>
                                    <Badge variant="outline" className="border-[var(--dash-border)]">{format(event.date, 'HH:mm')}</Badge>
                                  </div>
                                  <p className="text-sm text-[var(--dash-text-muted)]">{format(event.date, 'PPP')}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Calendar className="h-12 w-12 text-[var(--dash-text-muted)] mx-auto mb-4" />
                          <p className="text-[var(--dash-text)]">No upcoming events</p>
                        </div>
                      )}
                    </ScrollArea>
                  </div>
                </div>

                <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-[var(--dash-border)]">
                    <h2 className="text-lg font-semibold text-[var(--dash-text)]">Recent Activity</h2>
                    <p className="text-sm text-[var(--dash-text-muted)]">Latest updates</p>
                  </div>
                  <div className="p-6">
                    <ScrollArea className="h-[280px] pr-4">
                      {recentActivities.length > 0 ? (
                        <div className="space-y-4">
                          {recentActivities.map((a) => (
                            <div key={a.id} className="flex gap-4 items-start">
                              <div className="rounded-full p-2 bg-[var(--dash-accent)]/10 text-[var(--dash-accent)]"><Activity className="h-4 w-4" /></div>
                              <div className="flex-1">
                                <p className="font-medium text-[var(--dash-text)]">{a.title}</p>
                                <p className="text-sm text-[var(--dash-text-muted)]">{a.desc}</p>
                                <p className="text-xs text-[var(--dash-text-muted)] mt-1">{format(a.date, "MMM d, yyyy")}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Activity className="h-12 w-12 text-[var(--dash-text-muted)] mx-auto mb-4" />
                          <p className="text-[var(--dash-text)]">No recent activity</p>
                        </div>
                      )}
                    </ScrollArea>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-[var(--dash-border)] flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-semibold text-[var(--dash-text)]">Recent Clients</h2>
                    <p className="text-sm text-[var(--dash-text-muted)]">Your client database</p>
                  </div>
                  <Link to="/clients"><Button variant="outline" size="sm" className="border-[var(--dash-border)]">View All</Button></Link>
                </div>
                <div className="overflow-x-auto">
                  {clients.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[var(--dash-border)]">
                          <TableHead className="text-[var(--dash-text-muted)]">Client</TableHead>
                          <TableHead className="text-[var(--dash-text-muted)]">Email</TableHead>
                          <TableHead className="text-[var(--dash-text-muted)]">Phone</TableHead>
                          <TableHead className="text-[var(--dash-text-muted)]">Projects</TableHead>
                          <TableHead className="text-right w-20"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {clients.slice(0, 5).map((client: any) => (
                          <TableRow key={client.id} className="border-[var(--dash-border)]">
                            <TableCell className="font-medium">{client.firstName || client.first_name} {client.lastName || client.last_name}</TableCell>
                            <TableCell className="text-[var(--dash-text-muted)]">{client.email || '—'}</TableCell>
                            <TableCell className="text-[var(--dash-text-muted)]">{client.phone || '—'}</TableCell>
                            <TableCell>{projects.filter((p: any) => p.clientId === client.id || p.client_id === client.id).length}</TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild><Button variant="ghost" className="h-8 w-8 p-0"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="border-[var(--dash-border)]">
                                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                  <DropdownMenuItem asChild><Link to={`/clients/${client.id}`}>View Details</Link></DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem className="text-red-600">Delete</DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-12">
                      <Users className="h-12 w-12 text-[var(--dash-text-muted)] mx-auto mb-4" />
                      <p className="text-[var(--dash-text)]">No clients yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
